/**
 * Changing the signed-in account's password. Used by the merchant Security tab
 * and the Super Admin profile, which differ in everything around it.
 *
 * Confirmed inline rather than with a toast so it reads the same on both sides.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Card, Field, Notice } from './ui.js'
import { LockIcon } from './icons.js'

const MIN_PASSWORD = 12

export function ChangePasswordCard(): React.ReactNode {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const change = useMutation({
    mutationFn: () => api('/v1/auth/password', { method: 'POST', body: { currentPassword: current, newPassword: next } }),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setConfirm('')
    },
  })

  const mismatch = confirm.length > 0 && next !== confirm
  const ready = current.length > 0 && next.length >= MIN_PASSWORD && next === confirm

  return (
    <Card title="Change password" subtitle="Signs you out of every other device.">
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault()
          if (ready) change.mutate()
        }}
      >
        <Field label="Current password">
          <div className="input-group">
            <LockIcon size={16} />
            <input className="input" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
          </div>
        </Field>
        <Field label="New password" hint={`At least ${MIN_PASSWORD} characters. A passphrase beats a short scramble.`}>
          <input className="input" type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
        </Field>
        <Field label="Confirm new password">
          <input className="input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </Field>

        {mismatch && <Notice kind="error">The two new passwords do not match.</Notice>}
        {change.error && <Notice kind="error">{(change.error as Error).message}</Notice>}
        {change.isSuccess && <Notice kind="ok">Password changed. Every other session has been signed out.</Notice>}

        <button type="submit" className="btn btn--primary" disabled={!ready || change.isPending}>
          {change.isPending ? 'Updating…' : 'Update password'}
        </button>
      </form>
    </Card>
  )
}
